import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import Navbar from "@/components/Navbar";
import DuplicateBoardDialog from "@/components/DuplicateBoardDialog";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Copy, Plus, Trash2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [boards, setBoards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [desc, setDesc] = useState("");
  const [dupBoard, setDupBoard] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/boards");
      setBoards(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/boards", { title, description: desc });
      setOpen(false);
      setTitle("");
      setDesc("");
      toast.success("Board created");
      navigate(`/boards/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this board permanently?")) return;
    try {
      await api.delete(`/boards/${id}`);
      setBoards((bs) => bs.filter((b) => b.id !== id));
      toast.success("Board deleted");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    }
  };

  const onDuplicated = (data) => {
    setBoards((bs) => [data, ...bs]);
  };

  return (
    <div className="min-h-screen bg-white dark:bg-zinc-950 flex flex-col pb-20 sm:pb-0">
      <Navbar />
      <main className="flex-1 px-4 sm:px-12 py-6 sm:py-12 max-w-7xl w-full mx-auto" data-testid="dashboard-main">
        {/* Header - mobile first stack, row on sm+ */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between mb-8 sm:mb-12 border-b border-zinc-900 dark:border-zinc-200 pb-5 sm:pb-6">
          <div className="min-w-0">
            <div className="font-mono-tight text-[10px] sm:text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2 sm:mb-3 truncate">
              workspace / {user?.email}
            </div>
            <h1 className="font-display text-3xl sm:text-5xl font-black tracking-tighter text-zinc-950 dark:text-zinc-100">
              Your boards.
            </h1>
          </div>
          {/* Desktop new-board button */}
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <button
                className="hidden sm:inline-flex items-center gap-2 bg-black text-white px-5 py-3 text-sm font-medium hover:bg-zinc-800 transition-colors"
                data-testid="create-board-button"
              >
                <Plus className="h-4 w-4" /> New board
              </button>
            </DialogTrigger>
            <DialogContent className="rounded-none border-zinc-200 dark:border-zinc-800 sm:max-w-lg w-[calc(100vw-2rem)]">
              <DialogHeader>
                <DialogTitle className="font-display text-2xl font-bold tracking-tight">
                  Create new board
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={create} className="space-y-4 mt-2" data-testid="create-board-form">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-[0.1em] mb-2">Title</label>
                  <input
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    required
                    className="w-full border border-zinc-200 px-4 py-3 text-base sm:text-sm focus:outline-none focus:border-black"
                    placeholder="Q3 roadmap"
                    data-testid="create-board-title-input"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-[0.1em] mb-2">
                    Description (optional)
                  </label>
                  <textarea
                    value={desc}
                    onChange={(e) => setDesc(e.target.value)}
                    rows={3}
                    className="w-full border border-zinc-200 px-4 py-3 text-base sm:text-sm focus:outline-none focus:border-black resize-none"
                    data-testid="create-board-desc-input"
                  />
                </div>
                <DialogFooter className="gap-2 flex-row">
                  <button
                    type="button"
                    onClick={() => setOpen(false)}
                    className="flex-1 sm:flex-none px-5 py-3 sm:py-2.5 text-sm font-medium border border-zinc-200 hover:border-black transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 sm:flex-none px-5 py-3 sm:py-2.5 text-sm font-medium bg-black text-white hover:bg-zinc-800 transition-colors"
                    data-testid="create-board-submit"
                  >
                    Create
                  </button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className="text-sm text-zinc-500 font-mono-tight uppercase tracking-widest">Loading...</div>
        ) : boards.length === 0 ? (
          <div className="border border-dashed border-zinc-300 p-8 sm:p-16 text-center" data-testid="empty-boards">
            <div className="font-mono-tight text-[10px] sm:text-xs uppercase tracking-[0.2em] text-zinc-500 mb-3">
              empty workspace
            </div>
            <h2 className="font-display text-2xl sm:text-3xl font-black tracking-tighter mb-3">
              No boards yet.
            </h2>
            <p className="text-sm text-zinc-500 max-w-md mx-auto mb-6">
              Create your first board to start organising tasks into columns and cards.
            </p>
            <button
              onClick={() => setOpen(true)}
              className="inline-flex items-center gap-2 bg-black text-white px-6 py-3 text-sm font-medium hover:bg-zinc-800 transition-colors"
              data-testid="empty-create-board-button"
            >
              <Plus className="h-4 w-4" /> Create board
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6" data-testid="boards-grid">
            {boards.map((b) => (
              <div
                key={b.id}
                className="group border border-zinc-200 dark:border-zinc-800 hover:border-black dark:hover:border-white active:border-black transition-colors flex flex-col"
                data-testid={`board-card-${b.id}`}
              >
                <button
                  onClick={() => navigate(`/boards/${b.id}`)}
                  className="flex-1 text-left p-5 sm:p-6 hover:bg-zinc-50 dark:hover:bg-zinc-900 active:bg-zinc-50 dark:active:bg-zinc-900 transition-colors"
                  data-testid={`board-open-${b.id}`}
                >
                  <div className="font-mono-tight text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-3">
                    board
                  </div>
                  <h3 className="font-display text-xl sm:text-2xl font-bold tracking-tight mb-2 line-clamp-2 text-zinc-950 dark:text-zinc-100">
                    {b.title}
                  </h3>
                  {b.description && (
                    <p className="text-sm text-zinc-500 line-clamp-3">{b.description}</p>
                  )}
                </button>
                <div className="border-t border-zinc-200 dark:border-zinc-800 px-4 py-2.5 sm:py-2 flex items-center justify-between text-xs text-zinc-500">
                  <span className="font-mono-tight uppercase tracking-wider">
                    {new Date(b.created_at).toLocaleDateString()}
                  </span>
                  <div className="flex items-center gap-0.5">
                    <button
                      onClick={() => setDupBoard(b)}
                      className="p-2 -m-1 hover:text-black dark:hover:text-white active:text-black transition-colors"
                      data-testid={`board-duplicate-${b.id}`}
                      aria-label="Duplicate board"
                    >
                      <Copy className="h-4 w-4" />
                    </button>
                    {b.owner_id === user?.id && (
                      <button
                        onClick={() => del(b.id)}
                        className="p-2 -m-1 hover:text-red-500 active:text-red-500 transition-colors"
                        data-testid={`board-delete-${b.id}`}
                        aria-label="Delete board"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      <DuplicateBoardDialog
        open={!!dupBoard}
        onOpenChange={(v) => !v && setDupBoard(null)}
        board={dupBoard || {}}
        onDuplicated={onDuplicated}
      />

      {/* Mobile floating action button */}
      <button
        onClick={() => setOpen(true)}
        className="sm:hidden fixed bottom-5 right-5 z-20 h-14 w-14 bg-black text-white flex items-center justify-center shadow-lg hover:bg-zinc-800 active:bg-zinc-800 transition-colors"
        data-testid="create-board-fab"
        aria-label="Create new board"
      >
        <Plus className="h-6 w-6" />
      </button>
    </div>
  );
}
